import { useEffect, useState } from "react";
import { Save, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { invalidateSettings } from "../../hooks/useSettings";
import { translations } from "../../lib/i18n";
import { ImagePicker } from "./PostsPanel";

const FS_OPTIONS = [
    ["", "افتراضي"],
    ["sm", "صغير"],
    ["md", "متوسط"],
    ["lg", "كبير"],
    ["xl", "كبير جداً"],
];

const SECTIONS = [
    {
        id: "identity", title: "الشعار والهوية", open: true,
        fields: [
            { k: "img_logo", type: "image", label: "الشعار الرئيسي (Mero)" },
            { k: "slogan_ar", raw: true, fsKey: "hero_slogan", label: "الشعار التسويقي — عربي", ph: "مع ميرو… إطارك أمانك" },
            { k: "slogan_en", raw: true, fsKey: "hero_slogan", ltr: true, label: "الشعار التسويقي — إنجليزي", ph: "With Mero… your tire is your safety" },
        ],
    },
    {
        id: "hero", title: "الواجهة الرئيسية (الهيرو)",
        fields: [
            { k: "hero_image", type: "image", label: "صورة الواجهة الرئيسية" },
            { k: "hero_badge", label: "شارة التعريف الصغيرة أعلى العنوان" },
            { k: "hero_t1", label: "العنوان — السطر الأول" },
            { k: "hero_t2", label: "العنوان — السطر الذهبي" },
            { k: "hero_t3", label: "العنوان — السطر الثالث" },
            { k: "hero_sub", type: "textarea", label: "الوصف تحت العنوان" },
            { k: "hero_cta_order", label: "نص زر الطلب" },
            { k: "hero_cta_wa", label: "نص زر واتساب" },
            { k: "hero_stats", type: "array", label: "بطاقات الإحصائيات (4 عناصر)" },
            { k: "hero_hud_line", label: "شريط الصورة — السطر الرئيسي" },
            { k: "hero_hud_right", label: "شريط الصورة — النص الجانبي" },
            { k: "hero_float_t", label: "البطاقة العائمة — العنوان" },
            { k: "hero_float_s", label: "البطاقة العائمة — الوصف" },
        ],
    },
    {
        id: "mobile", title: "قسم الخدمة المتنقلة",
        fields: [
            { k: "img_mobile", type: "image", label: "صورة البنشر المتنقل" },
            { k: "mob_badge", label: "الشارة الحمراء" },
            { k: "mob_t1", label: "العنوان — السطر الأول" },
            { k: "mob_t2", label: "العنوان — السطر الملوّن" },
            { k: "mob_desc", type: "textarea", label: "الوصف" },
            { k: "mob_places", type: "array", label: "أماكن الخدمة (3 عناصر)" },
            { k: "mob_cta", label: "نص زر الطلب" },
            { k: "mob_img_cap", label: "تعليق الصورة" },
        ],
    },
    {
        id: "inquiry", title: "قسم نموذج الاستفسار",
        fields: [
            { k: "inq_t1", label: "العنوان — السطر الأول" },
            { k: "inq_t2", label: "العنوان — السطر الذهبي" },
            { k: "inq_desc", type: "textarea", label: "الوصف" },
            { k: "inq_perks", type: "array", label: "المزايا (4 عناصر)" },
            { k: "inq_form_title", label: "عنوان النموذج" },
            { k: "inq_submit", label: "نص زر الإرسال" },
        ],
    },
    {
        id: "brands", title: "قسم الماركات و TESCHE",
        fields: [
            { k: "img_tesche", type: "image", label: "صورة خلفية بطاقة TESCHE" },
            { k: "br_title", label: "عنوان القسم" },
            { k: "br_desc", type: "textarea", label: "وصف القسم" },
            { k: "br_tesche_title", label: "عنوان بطاقة TESCHE" },
            { k: "br_tesche_desc", type: "textarea", label: "وصف بطاقة TESCHE" },
            { k: "br_tesche_cta", label: "زر بطاقة TESCHE" },
        ],
    },
    {
        id: "services", title: "قسم خدماتنا (5 بطاقات)",
        fields: [
            { k: "srv_title", label: "عنوان القسم" },
            { k: "srv_desc", type: "textarea", label: "وصف القسم" },
            ...[1, 2, 3, 4, 5].flatMap((n) => [
                { k: `srv_c${n}_t`, label: `البطاقة ${n} — العنوان` },
                { k: `srv_c${n}_d`, type: "textarea", label: `البطاقة ${n} — الوصف` },
            ]),
        ],
    },
    {
        id: "location", title: "قسم موقعنا",
        fields: [{ k: "loc_title", label: "عنوان القسم" }],
    },
    {
        id: "gallery", title: "معرض المحل",
        fields: [
            { k: "img_gallery_1", type: "image", label: "صورة المعرض 1" },
            { k: "img_gallery_2", type: "image", label: "صورة المعرض 2" },
            { k: "img_gallery_3", type: "image", label: "صورة المعرض 3" },
            { k: "gal_title", label: "عنوان القسم" },
            { k: "gal_desc", type: "textarea", label: "وصف القسم" },
            { k: "gal_caps", type: "array", label: "تعليقات الصور (3 عناصر)" },
        ],
    },
    {
        id: "blog", title: "قسم المدونة",
        fields: [
            { k: "blog_title", label: "عنوان القسم" },
            { k: "blog_desc", type: "textarea", label: "وصف القسم" },
        ],
    },
    {
        id: "footer", title: "الفوتر",
        fields: [
            { k: "ftr_tagline", type: "textarea", label: "نص التعريف بالشركة" },
            { k: "ftr_rights", label: "سطر الحقوق" },
        ],
    },
];

export const CustomizePanel = () => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings")
            .then((r) => {
                const s = r.data;
                const merged = { ...s };
                SECTIONS.forEach((sec) =>
                    sec.fields.forEach((f) => {
                        if (f.type === "image") return;
                        if (f.raw) {
                            if (!merged[f.k] && f.fsKey) {
                                merged[f.k] = (f.ltr ? translations.en : translations.ar)[f.fsKey] || "";
                            }
                            return;
                        }
                        const arK = `txt_${f.k}`;
                        const enK = `txt_${f.k}_en`;
                        if (!merged[arK]) {
                            const d = translations.ar[f.k];
                            merged[arK] = Array.isArray(d) ? d.join(" | ") : d || "";
                        }
                        if (!merged[enK]) {
                            const d = translations.en[f.k];
                            merged[enK] = Array.isArray(d) ? d.join(" | ") : d || "";
                        }
                    })
                );
                setForm(merged);
            })
            .catch(() => setForm({}));
    }, []);

    if (!form) return <p className="text-slate-500 text-sm">جاري التحميل...</p>;

    const setKey = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings", form);
            invalidateSettings();
            toast.success("تم الحفظ — افتح الموقع في تبويب جديد لرؤية التغييرات فوراً");
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    const sizeSelect = (fsKey) => (
        <div className="mb-2 flex items-center gap-3">
            <span className="text-[11px] font-bold text-slate-500">حجم الخط:</span>
            <select
                value={form[`fs_${fsKey}`] || ""}
                onChange={(e) => setKey(`fs_${fsKey}`, e.target.value)}
                data-testid={`fs-${fsKey}`}
                className="bg-[#0A1228]/70 border border-white/10 rounded-lg py-1.5 px-3 text-xs text-white cursor-pointer focus:outline-none focus:border-amber-400/60"
            >
                {FS_OPTIONS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                ))}
            </select>
        </div>
    );

    const renderField = (f) => {
        if (f.type === "image") {
            return (
                <div key={f.k} className="mb-4">
                    <p className="text-xs font-bold text-slate-400 mb-2">{f.label}</p>
                    <ImagePicker value={form[f.k] || ""} onChange={(v) => setKey(f.k, v)} testid={`cust-${f.k}`} />
                </div>
            );
        }
        if (f.raw) {
            return (
                <div key={f.k} className="mb-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <p className="text-xs font-bold text-slate-400">{f.label}</p>
                        {f.fsKey && sizeSelect(f.fsKey)}
                    </div>
                    <input value={form[f.k] || ""} onChange={(e) => setKey(f.k, e.target.value)} placeholder={f.ph || "اتركه فارغاً للنص الافتراضي"} data-testid={`cust-${f.k}`} className={inputCls} dir={f.ltr ? "ltr" : "rtl"} />
                </div>
            );
        }
        const kAr = `txt_${f.k}`;
        const kEn = `txt_${f.k}_en`;
        const Cmp = f.type === "textarea" ? "textarea" : "input";
        const extra = f.type === "textarea" ? { className: `${inputCls} h-20 resize-none` } : { className: inputCls };
        return (
            <div key={f.k} className="mb-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <p className="text-xs font-bold text-slate-400">{f.label}</p>
                    {sizeSelect(f.k)}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <p className="text-[10px] text-slate-600 mb-1">عربي</p>
                        <Cmp value={form[kAr] || ""} onChange={(e) => setKey(kAr, e.target.value)} placeholder={f.type === "array" ? "افصل العناصر بـ |" : ""} data-testid={`cust-${f.k}-ar`} {...extra} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-600 mb-1">English</p>
                        <Cmp value={form[kEn] || ""} onChange={(e) => setKey(kEn, e.target.value)} placeholder={f.type === "array" ? "Separate items with |" : ""} data-testid={`cust-${f.k}-en`} dir="ltr" {...extra} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div data-testid="admin-customize-panel" className="max-w-3xl">
            <h2 className="font-display font-bold text-xl text-white mb-2">تخصيص الموقع</h2>
            <p className="text-sm text-slate-500 mb-2">
                عدّل أي نص أو صورة في أي قسم — الحقل الفارغ يستخدم النص الافتراضي تلقائياً.
            </p>
            <p className="text-xs text-amber-400/80 mb-6">
                ملاحظة: صور المقالات تُعدّل من تبويب المقالات، وصور الإطارات من تبويب الإطارات.
            </p>

            <div className="space-y-3">
                {SECTIONS.map((sec) => (
                    <details key={sec.id} open={sec.open} className="spotlight-card border border-white/10 rounded-2xl overflow-hidden" data-testid={`cust-section-${sec.id}`}>
                        <summary className="cursor-pointer list-none flex items-center justify-between px-5 py-4 hover:bg-white/[0.03] transition-colors duration-200">
                            <span className="font-display font-bold text-white text-sm">{sec.title}</span>
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                        </summary>
                        <div className="px-5 pb-5 pt-1 border-t border-white/5">
                            {sec.fields.map(renderField)}
                        </div>
                    </details>
                ))}
            </div>

            <button
                onClick={save}
                disabled={saving}
                data-testid="customize-save-button"
                className="sticky bottom-4 mt-6 w-full flex items-center justify-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl py-4 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50 shadow-[0_10px_40px_rgba(250,204,21,0.25)]"
            >
                <Save className="w-4 h-4" />
                {saving ? "جاري الحفظ..." : "حفظ كل التخصيصات"}
            </button>
        </div>
    );
};
